// src/shared/services/apiService.js
import axios from 'axios';
import { ServiceError, ErrorTypes } from './types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_TIMEOUT = 30000;
const USE_LOCAL_AUTH = process.env.REACT_APP_USE_LOCAL_AUTH === 'true';

console.log('apiService: USE_LOCAL_AUTH =', USE_LOCAL_AUTH);
console.log('apiService: API_BASE_URL =', API_BASE_URL);

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];
let cognitoClient = null;

const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 < Date.now();
    } catch {
        return true;
    }
};

const subscribeTokenRefresh = (callback) => {
    refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token) => {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
};

// Lazy-load Cognito client only when needed (not in local auth mode)
const getCognitoClient = async () => {
    if (USE_LOCAL_AUTH) {
        return null;
    }
    if (!cognitoClient) {
        const { CognitoIdentityProviderClient } = await import("@aws-sdk/client-cognito-identity-provider");
        cognitoClient = new CognitoIdentityProviderClient({
            region: process.env.REACT_APP_COGNITO_REGION
        });
    }
    return cognitoClient;
};

const refreshTokens = async () => {
    // Skip Cognito refresh in local auth mode
    if (USE_LOCAL_AUTH) {
        throw new Error('Token refresh not available in local mode');
    }

    const refreshToken = localStorage.getItem('RefreshToken');
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    const { InitiateAuthCommand } = await import("@aws-sdk/client-cognito-identity-provider");
    const client = await getCognitoClient();

    const params = {
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
        AuthParameters: {
            REFRESH_TOKEN: refreshToken,
        },
    };

    const command = new InitiateAuthCommand(params);
    const { AuthenticationResult } = await client.send(command);

    if (AuthenticationResult) {
        localStorage.setItem("IdToken", AuthenticationResult.IdToken || '');
        localStorage.setItem("AccessToken", AuthenticationResult.AccessToken || '');
        return AuthenticationResult.IdToken;
    }

    throw new Error('Failed to refresh token');
};

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: DEFAULT_TIMEOUT,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor
api.interceptors.request.use(
    async (config) => {
        // Skip auth header for presigned URL uploads
        const isPresignedUrlUpload = (config.url?.includes(process.env.REACT_APP_S3_ENDPOINT_URL) && config.method === 'put');

        if (!isPresignedUrlUpload) {
            let token = localStorage.getItem('IdToken');

            // In local auth mode, tokens are not JWTs (e.g., username/UUID strings).
            // Skip expiration check and refresh entirely — just attach the token as-is.
            if (USE_LOCAL_AUTH) {
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } else {
                // Proactively refresh if token is expired (avoids CORS-blocked 401s from API Gateway)
                // Only attempt refresh/redirect if a token actually existed — skip for guest users (no token at all)
                if (token && isTokenExpired(token)) {
                    const refreshToken = localStorage.getItem('RefreshToken');
                    if (!refreshToken) {
                        localStorage.removeItem('IdToken');
                        localStorage.removeItem('AccessToken');
                        window.location.href = '/login';
                        return Promise.reject(new ServiceError(ErrorTypes.AUTH, 'Session expired - please login again'));
                    }

                    if (isRefreshing) {
                        token = await new Promise((resolve) => {
                            subscribeTokenRefresh(resolve);
                        });
                    } else {
                        isRefreshing = true;
                        try {
                            token = await refreshTokens();
                            isRefreshing = false;
                            onTokenRefreshed(token);
                        } catch {
                            isRefreshing = false;
                            localStorage.removeItem('IdToken');
                            localStorage.removeItem('AccessToken');
                            localStorage.removeItem('RefreshToken');
                            window.location.href = '/login';
                            return Promise.reject(new ServiceError(ErrorTypes.AUTH, 'Session expired - please login again'));
                        }
                    }
                }

                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            }
        }

        // Handle file uploads
        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = 'multipart/form-data';
        }

        // Add timestamp to prevent caching
        if (config.method === 'get') {
            config.params = {
                ...config.params,
                _t: Date.now()
            };
        }

        return config;
    },
    (error) => {
        return Promise.reject(new ServiceError(ErrorTypes.SERVER, error.message));
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Handle network errors
        if (!error.response) {
            return Promise.reject(
                new ServiceError(
                    ErrorTypes.SERVER,
                    'Network error - please check your connection'
                )
            );
        }

        // Handle 401 with token refresh (only in production mode with Cognito)
        if (error.response.status === 401 && !originalRequest._retry && !USE_LOCAL_AUTH) {
            // Check if we have a refresh token
            const refreshToken = localStorage.getItem('RefreshToken');
            if (!refreshToken) {
                // No refresh token, redirect to login
                localStorage.removeItem('IdToken');
                localStorage.removeItem('AccessToken');
                window.location.href = '/login';
                return Promise.reject(
                    new ServiceError(ErrorTypes.AUTH, 'Session expired - please login again')
                );
            }

            if (isRefreshing) {
                // Wait for the ongoing refresh to complete
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(api(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newToken = await refreshTokens();
                isRefreshing = false;
                onTokenRefreshed(newToken);

                // Retry original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                // Refresh failed, clear tokens and redirect to login
                localStorage.removeItem('IdToken');
                localStorage.removeItem('AccessToken');
                localStorage.removeItem('RefreshToken');
                window.location.href = '/login';
                return Promise.reject(
                    new ServiceError(ErrorTypes.AUTH, 'Session expired - please login again')
                );
            }
        }

        // Handle different error status codes
        switch (error.response.status) {
            case 401:
                // Already handled above, but just in case
                return Promise.reject(
                    new ServiceError(ErrorTypes.AUTH, 'Session expired - please login again')
                );

            case 403:
                // Forbidden
                return Promise.reject(
                    new ServiceError(ErrorTypes.AUTH, 'You do not have permission to perform this action')
                );

            case 404:
                // Not Found
                return Promise.reject(
                    new ServiceError(ErrorTypes.NOT_FOUND, 'The requested resource was not found')
                );

            case 422:
                // Validation Error
                return Promise.reject(
                    new ServiceError(
                        ErrorTypes.VALIDATION,
                        error.response.data.message || 'Validation failed'
                    )
                );

            case 429:
                // Rate Limiting
                return Promise.reject(
                    new ServiceError(
                        ErrorTypes.SERVER,
                        'Too many requests - please try again later'
                    )
                );

            default:
                // Server Error
                return Promise.reject(
                    new ServiceError(
                        ErrorTypes.SERVER,
                        error.response.data.message || 'An unexpected error occurred'
                    )
                );
        }
    }
);

// Helper methods for common API operations
const apiService = {
    // Base axios instance
    instance: api,

    // GET request
    async get(url, config = {}) {
        try {
            const response = await api.get(url, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // POST request
    async post(url, data = {}, config = {}) {
        try {
            const response = await api.post(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // PUT request
    async put(url, data = {}, config = {}) {
        try {
            const response = await api.put(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // PATCH request
    async patch(url, data = {}, config = {}) {
        try {
            const response = await api.patch(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // DELETE request
    async delete(url, config = {}) {
        try {
            const response = await api.delete(url, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Upload file(s)
    async upload(url, files, onProgress = null) {
        const formData = new FormData();

        if (Array.isArray(files)) {
            files.forEach((file, index) => {
                formData.append(`file${index}`, file);
            });
        } else {
            formData.append('file', files);
        }

        try {
            const response = await api.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: onProgress ? (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onProgress(percentCompleted);
                } : undefined
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Download file
    async download(url, filename) {
        try {
            const response = await api.get(url, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            return true;
        } catch (error) {
            throw error;
        }
    },

    // Update competition
    async updateCompetition(competitionId, competitionData) {
        return this.put(`/competitions/${competitionId}`, competitionData);
    }
};

export default apiService;
